import {useCallback, useEffect, useState} from 'react'
import {createPortal} from 'react-dom'
import {useQueryClient} from '@tanstack/react-query'
import {toast} from 'react-toastify'
import {toastDefaultConfig} from '@/shared/config/toastConfig'
import {formatBytes, getLogger} from '@/shared/utils'
import {importsQueryKey} from '@/shared/utils/hooks/useAPIFiles'
import {
  browseExternalS3,
  importFromS3,
  BrowseItem,
  ImportSourceType,
} from '../core/_requests'

interface ImportWizardProps {
  destinationPrefix: string
  identityId: string
  initialSourceType?: ImportSourceType
  onClose: () => void
  onComplete: () => void
}

type AuthMode = 'public' | 'accessKeys'

interface S3Config {
  s3Uri: string
  authMode: AuthMode
  accessKeyId: string
  secretAccessKey: string
  requesterPays: boolean
}

interface HttpsConfig {
  urls: string
  authToken: string
}

interface FtpConfig {
  host: string
  port: number
  user: string
  password: string
  path: string
  secure: boolean
}

/** Parse ftp:// or https://ftp.* URLs into host + path for the FTP form. */
function parseFtpUrl(input: string): {host: string; path: string; port: number} | null {
  const trimmed = input.trim()
  if (trimmed.startsWith('ftp://')) {
    try {
      const url = new URL(trimmed)
      return {host: url.hostname, port: parseInt(url.port) || 21, path: url.pathname || '/'}
    } catch {
      return null
    }
  }
  if (trimmed.startsWith('https://') || trimmed.startsWith('http://')) {
    try {
      const url = new URL(trimmed)
      if (url.hostname.startsWith('ftp.') || url.hostname.includes('ftp')) {
        return {host: url.hostname, port: 21, path: url.pathname || '/'}
      }
    } catch {
      return null
    }
  }
  return null
}

function parseS3Uri(input: string): {bucket: string; prefix: string} {
  let cleaned = input.trim()
  if (cleaned.startsWith('s3://')) cleaned = cleaned.slice(5)
  else if (cleaned.startsWith('s3a://')) cleaned = cleaned.slice(6)
  else if (cleaned.startsWith('https://') || cleaned.startsWith('http://')) {
    try {
      const url = new URL(cleaned)
      const host = url.hostname
      if (host.endsWith('.s3.amazonaws.com')) {
        const bucket = host.replace('.s3.amazonaws.com', '')
        return {bucket, prefix: url.pathname.replace(/^\//, '')}
      }
      if (host.match(/s3[.-][\w-]+\.amazonaws\.com/)) {
        const parts = url.pathname.replace(/^\//, '').split('/')
        return {bucket: parts[0], prefix: parts.slice(1).join('/')}
      }
    } catch {
      /* fall through */
    }
  }
  const slashIdx = cleaned.indexOf('/')
  if (slashIdx === -1) return {bucket: cleaned, prefix: ''}
  return {bucket: cleaned.slice(0, slashIdx), prefix: cleaned.slice(slashIdx + 1)}
}

export const ImportWizard = ({
  destinationPrefix,
  identityId,
  initialSourceType,
  onClose,
  onComplete,
}: ImportWizardProps) => {
  const queryClient = useQueryClient()
  const [step, setStep] = useState<1 | 2 | 3>(1)
  const sourceType = initialSourceType || 's3'

  const [s3Config, setS3Config] = useState<S3Config>({
    s3Uri: '',
    authMode: 'public',
    accessKeyId: '',
    secretAccessKey: '',
    requesterPays: false,
  })
  const [httpsConfig, setHttpsConfig] = useState<HttpsConfig>({urls: '', authToken: ''})
  const [ftpConfig, setFtpConfig] = useState<FtpConfig>({
    host: '',
    port: 21,
    user: 'anonymous',
    password: 'anonymous@',
    path: '/',
    secure: false,
  })

  // Step 2 state (S3 / FTP browsing)
  const [browseItems, setBrowseItems] = useState<BrowseItem[]>([])
  const [browsePath, setBrowsePath] = useState<string[]>([])
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())
  const [isBrowsing, setIsBrowsing] = useState(false)
  const [browseError, setBrowseError] = useState<string | null>(null)

  // Step 3 state
  const [isImporting, setIsImporting] = useState(false)

  const parsed = parseS3Uri(s3Config.s3Uri)
  const currentPrefix = browsePath.length > 0 ? browsePath[browsePath.length - 1] : parsed.prefix

  const needsBrowseStep = sourceType === 's3' || sourceType === 'ftp'
  const isSingleStep = sourceType === 'https'

  const doBrowse = useCallback(
    async (prefix?: string) => {
      setIsBrowsing(true)
      setBrowseError(null)
      try {
        if (sourceType === 's3') {
          const {bucket} = parseS3Uri(s3Config.s3Uri)
          const result = await browseExternalS3({
            sourceType: 's3',
            sourceBucket: bucket,
            sourcePrefix: prefix ?? currentPrefix,
            accessKeyId: s3Config.authMode === 'accessKeys' ? s3Config.accessKeyId : undefined,
            secretAccessKey:
              s3Config.authMode === 'accessKeys' ? s3Config.secretAccessKey : undefined,
            requesterPays: s3Config.requesterPays || undefined,
          })
          if (result.error) {
            setBrowseError(result.error)
            setBrowseItems([])
          } else {
            setBrowseItems(result.items || [])
          }
        } else if (sourceType === 'ftp') {
          const result = await browseExternalS3({
            sourceType: 'ftp',
            ftpHost: ftpConfig.host,
            ftpPort: ftpConfig.port,
            ftpUser: ftpConfig.user,
            ftpPassword: ftpConfig.password,
            ftpPath: prefix ?? ftpConfig.path,
            ftpSecure: ftpConfig.secure || undefined,
          })
          if (result.error) {
            setBrowseError(result.error)
            setBrowseItems([])
          } else {
            setBrowseItems(result.items || [])
          }
        }
      } catch (err: any) {
        setBrowseError(err.message || 'Failed to browse')
        setBrowseItems([])
      } finally {
        setIsBrowsing(false)
      }
    },
    [sourceType, s3Config, ftpConfig, currentPrefix]
  )

  const handleNext = async () => {
    if (step === 1) {
      if (sourceType === 's3') {
        const {bucket, prefix} = parseS3Uri(s3Config.s3Uri)
        if (!bucket.trim()) {
          toast.error('Please enter an S3 URI or bucket name', toastDefaultConfig)
          return
        }
        if (
          s3Config.authMode === 'accessKeys' &&
          (!s3Config.accessKeyId.trim() || !s3Config.secretAccessKey.trim())
        ) {
          toast.error('Access Key and Secret Key are required', toastDefaultConfig)
          return
        }
        setStep(2)
        setBrowsePath(prefix ? [prefix] : [])
        await doBrowse(prefix || undefined)
      } else if (sourceType === 'https') {
        const urls = httpsConfig.urls
          .split('\n')
          .map((u) => u.trim())
          .filter(Boolean)
        if (urls.length === 0) {
          toast.error('Please enter at least one URL', toastDefaultConfig)
          return
        }
        for (const url of urls) {
          if (!url.startsWith('http://') && !url.startsWith('https://')) {
            toast.error(`Invalid URL: ${url}`, toastDefaultConfig)
            return
          }
        }
        await handleImport()
        return
      } else if (sourceType === 'ftp') {
        if (!ftpConfig.host.trim()) {
          toast.error('FTP host is required', toastDefaultConfig)
          return
        }
        setStep(2)
        setBrowsePath([ftpConfig.path])
        await doBrowse(ftpConfig.path)
      }
    } else if (step === 2) {
      if (selectedKeys.size === 0) {
        toast.error('Select at least one file or folder to import', toastDefaultConfig)
        return
      }
      setStep(3)
    }
  }

  const handleBack = () => {
    if (step === 2) {
      setStep(1)
      setBrowseItems([])
      setSelectedKeys(new Set())
      setBrowseError(null)
    } else if (step === 3) {
      setStep(needsBrowseStep ? 2 : 1)
    }
  }

  const navigateToFolder = async (folderPath: string) => {
    setBrowsePath((prev) => [...prev, folderPath])
    setSelectedKeys(new Set())
    await doBrowse(folderPath)
  }

  const navigateUp = async () => {
    if (browsePath.length <= 1) {
      setBrowsePath([])
      await doBrowse(sourceType === 'ftp' ? '/' : '')
    } else {
      const newPath = browsePath.slice(0, -1)
      setBrowsePath(newPath)
      await doBrowse(newPath[newPath.length - 1])
    }
  }

  const toggleSelection = (key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }

  const selectAll = () => {
    if (selectedKeys.size === browseItems.length) {
      setSelectedKeys(new Set())
    } else {
      setSelectedKeys(new Set(browseItems.map((item) => item.path)))
    }
  }

  const handleImport = async () => {
    setIsImporting(true)
    try {
      let result: any

      if (sourceType === 's3') {
        const {bucket} = parseS3Uri(s3Config.s3Uri)
        result = await importFromS3({
          sourceType: 's3',
          sourceBucket: bucket,
          sourceKeys: Array.from(selectedKeys),
          accessKeyId: s3Config.authMode === 'accessKeys' ? s3Config.accessKeyId : undefined,
          secretAccessKey:
            s3Config.authMode === 'accessKeys' ? s3Config.secretAccessKey : undefined,
          destinationPrefix,
          requesterPays: s3Config.requesterPays || undefined,
        })
      } else if (sourceType === 'https') {
        const urls = httpsConfig.urls
          .split('\n')
          .map((u) => u.trim())
          .filter(Boolean)
        result = await importFromS3({
          sourceType: 'https',
          urls,
          accessKeyId: httpsConfig.authToken || undefined,
          destinationPrefix,
        })
      } else if (sourceType === 'ftp') {
        result = await importFromS3({
          sourceType: 'ftp',
          ftpHost: ftpConfig.host,
          ftpPort: ftpConfig.port,
          ftpUser: ftpConfig.user,
          ftpPassword: ftpConfig.password,
          ftpPaths: Array.from(selectedKeys),
          ftpSecure: ftpConfig.secure || undefined,
          destinationPrefix,
        })
      }

      if (result?.error) {
        toast.error(`Import failed: ${result.error}`, toastDefaultConfig)
      } else if (result?.status === 'started') {
        toast.success(
          'Import started. Follow it under Imports in the Files header; you will be notified when it finishes.',
          toastDefaultConfig
        )
        queryClient.invalidateQueries(importsQueryKey)
        onComplete()
      } else {
        const msg =
          (result?.totalFailed ?? 0) > 0
            ? `Imported ${result.totalCopied} file(s), ${result.totalFailed} failed.`
            : `Successfully imported ${result.totalCopied} file(s).`
        toast.success(msg, toastDefaultConfig)
        onComplete()
      }
    } catch (err: any) {
      getLogger().error('Import error', err)
      toast.error(`Import failed: ${err.message}`, toastDefaultConfig)
    } finally {
      setIsImporting(false)
    }
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        if (!isImporting) onClose()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose, isImporting])

  const selectedItems = browseItems.filter((i) => selectedKeys.has(i.path))

  const sourceLabel =
    sourceType === 's3' ? 'Import from S3' : sourceType === 'https' ? 'Import from URL' : 'Import from FTP'
  const sourceIcon =
    sourceType === 's3'
      ? 'fas fa-cloud-download-alt'
      : sourceType === 'https'
        ? 'fas fa-link'
        : 'fas fa-server'

  const stepCount = isSingleStep ? 1 : needsBrowseStep ? 3 : 2
  const stepLabels = isSingleStep
    ? ['Import']
    : needsBrowseStep
      ? ['Source', 'Select', 'Import']
      : ['Source & Files', 'Import']

  // Portal to body so the wizard escapes .ob-files-panel (z-index: 5) and sits above the header.
  return createPortal(
    <>
      <div className='modal-backdrop display-block show'></div>
      <div className='modal fade display-block show'>
        <div className='modal-dialog modal-dialog-centered ob-dialog'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h5 className='modal-title'>
                <i className={`${sourceIcon} me-2`}></i>
                {sourceLabel}
              </h5>
              {!isSingleStep && (
                <div style={{display: 'flex', gap: '0.75rem', alignItems: 'center'}}>
                  {stepLabels.map((label, idx) => {
                    const s = idx + 1
                    return (
                      <div
                        key={s}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.35rem',
                          opacity: step >= s ? 1 : 0.4,
                        }}
                      >
                        <span
                          style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            background: step === s ? '#888e00' : step > s ? '#6c9d3a' : '#ccc',
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 'var(--ob-font-size-xs)',
                            fontWeight: 600,
                          }}
                        >
                          {step > s ? (
                            <i className='fas fa-check' style={{fontSize: 'var(--ob-font-size-xs)'}}></i>
                          ) : (
                            s
                          )}
                        </span>
                        <span style={{fontSize: 'var(--ob-font-size-xs)', fontWeight: 500}}>{label}</span>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>

            <div
              className='modal-body'
              style={{maxHeight: '60vh', overflowY: 'auto'}}
            >
              {step === 1 && (
                <SourceForm
                  sourceType={sourceType}
                  s3Config={s3Config}
                  setS3Config={setS3Config}
                  httpsConfig={httpsConfig}
                  setHttpsConfig={setHttpsConfig}
                  ftpConfig={ftpConfig}
                  setFtpConfig={setFtpConfig}
                />
              )}
              {step === 2 && needsBrowseStep && (
                <FileBrowser
                  items={browseItems}
                  selectedKeys={selectedKeys}
                  browsePath={browsePath}
                  isLoading={isBrowsing}
                  error={browseError}
                  onNavigate={navigateToFolder}
                  onNavigateUp={navigateUp}
                  onToggle={toggleSelection}
                  onSelectAll={selectAll}
                />
              )}
              {((step === 3 && needsBrowseStep) || (step === 2 && !needsBrowseStep)) && (
                <ImportSummary
                  sourceType={sourceType}
                  s3Config={s3Config}
                  httpsConfig={httpsConfig}
                  ftpConfig={ftpConfig}
                  selectedItems={selectedItems}
                  selectedKeys={selectedKeys}
                  destination={destinationPrefix}
                  isImporting={isImporting}
                />
              )}
            </div>

            <div className='modal-footer'>
              <button className='btn btn-light' onClick={onClose} disabled={isImporting}>
                Cancel
              </button>
              {step > 1 && (
                <button className='btn btn-light' onClick={handleBack} disabled={isImporting}>
                  <i className='fas fa-arrow-left me-1'></i> Back
                </button>
              )}
              {isSingleStep ? (
                <button
                  className='btn btn-primary'
                  onClick={handleNext}
                  disabled={isImporting || !httpsConfig.urls.trim()}
                >
                  {isImporting ? (
                    <>
                      <span className='spinner-border spinner-border-sm me-2'></span>
                      Importing...
                    </>
                  ) : (
                    <>
                      <i className='fas fa-cloud-download-alt me-1'></i> Start Import
                    </>
                  )}
                </button>
              ) : step < stepCount ? (
                <button className='btn btn-primary' onClick={handleNext} disabled={isBrowsing}>
                  Next <i className='fas fa-arrow-right ms-1'></i>
                </button>
              ) : (
                <button
                  className='btn btn-primary'
                  onClick={handleImport}
                  disabled={isImporting || (needsBrowseStep && selectedKeys.size === 0)}
                >
                  {isImporting ? (
                    <>
                      <span className='spinner-border spinner-border-sm me-2'></span>
                      Importing...
                    </>
                  ) : (
                    <>
                      <i className='fas fa-cloud-download-alt me-1'></i> Start Import
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body,
  )
}

/* ---------- Step 1: Source Configuration ---------- */

interface SourceFormProps {
  sourceType: ImportSourceType
  s3Config: S3Config
  setS3Config: React.Dispatch<React.SetStateAction<S3Config>>
  httpsConfig: HttpsConfig
  setHttpsConfig: React.Dispatch<React.SetStateAction<HttpsConfig>>
  ftpConfig: FtpConfig
  setFtpConfig: React.Dispatch<React.SetStateAction<FtpConfig>>
}

const SourceForm = ({
  sourceType,
  s3Config,
  setS3Config,
  httpsConfig,
  setHttpsConfig,
  ftpConfig,
  setFtpConfig,
}: SourceFormProps) => {
  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: '1rem'}}>
      {sourceType === 's3' && <S3SourceForm config={s3Config} setConfig={setS3Config} />}
      {sourceType === 'https' && (
        <HttpsSourceForm config={httpsConfig} setConfig={setHttpsConfig} />
      )}
      {sourceType === 'ftp' && <FtpSourceForm config={ftpConfig} setConfig={setFtpConfig} />}
    </div>
  )
}

/* ---------- S3 source sub-form ---------- */

const S3SourceForm = ({
  config,
  setConfig,
}: {
  config: S3Config
  setConfig: React.Dispatch<React.SetStateAction<S3Config>>
}) => (
  <div style={{display: 'flex', flexDirection: 'column', gap: '1rem'}}>
    <div>
      <label className='form-label fw-semibold'>S3 URI or Bucket Name</label>
      <input
        autoFocus
        className='form-control'
        type='text'
        placeholder='s3://my-bucket/datasets/rnaseq/'
        value={config.s3Uri}
        onChange={(e) => setConfig((prev) => ({...prev, s3Uri: e.target.value}))}
      />
      <div className='form-text'>
        Paste an S3 URI (e.g. <code>s3://noaa-ghcn-pds/csv/</code>) or just a bucket name. Region is
        detected automatically.
      </div>
    </div>
    <div className='form-check' style={{marginTop: '0.25rem'}}>
      <input
        className='form-check-input'
        type='checkbox'
        id='requester-pays'
        checked={config.requesterPays}
        onChange={(e) => setConfig((prev) => ({...prev, requesterPays: e.target.checked}))}
      />
      <label className='form-check-label' htmlFor='requester-pays'>
        Requester-pays bucket
      </label>
      <div className='form-text'>
        Enable for public datasets like NCBI SRA, 1000 Genomes, etc.
      </div>
    </div>
    <hr style={{margin: '0.25rem 0'}} />
    <div>
      <label className='form-label fw-semibold'>Authentication</label>
      <div style={{display: 'flex', gap: '1rem'}}>
        <div className='form-check'>
          <input
            className='form-check-input'
            type='radio'
            id='auth-public'
            checked={config.authMode === 'public'}
            onChange={() => setConfig((prev) => ({...prev, authMode: 'public'}))}
          />
          <label className='form-check-label' htmlFor='auth-public'>
            Public (no credentials)
          </label>
        </div>
        <div className='form-check'>
          <input
            className='form-check-input'
            type='radio'
            id='auth-keys'
            checked={config.authMode === 'accessKeys'}
            onChange={() => setConfig((prev) => ({...prev, authMode: 'accessKeys'}))}
          />
          <label className='form-check-label' htmlFor='auth-keys'>
            Access Keys
          </label>
        </div>
      </div>
    </div>
    {config.authMode === 'accessKeys' && (
      <>
        <div>
          <label className='form-label fw-semibold'>Access Key ID</label>
          <input
            className='form-control form-control-sm'
            type='text'
            placeholder='AKIAIOSFODNN7EXAMPLE'
            value={config.accessKeyId}
            onChange={(e) => setConfig((prev) => ({...prev, accessKeyId: e.target.value}))}
            autoComplete='off'
          />
        </div>
        <div>
          <label className='form-label fw-semibold'>Secret Access Key</label>
          <input
            className='form-control form-control-sm'
            type='password'
            placeholder='wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY'
            value={config.secretAccessKey}
            onChange={(e) => setConfig((prev) => ({...prev, secretAccessKey: e.target.value}))}
            autoComplete='off'
          />
        </div>
        <div
          className='alert alert-light-warning d-flex align-items-center p-3'
          style={{fontSize: 'var(--ob-font-size-xs)'}}
        >
          <i className='fas fa-shield-alt me-2'></i>
          Credentials are used only for this transfer and are never stored.
        </div>
      </>
    )}
  </div>
)

/* ---------- HTTPS source sub-form ---------- */

const HttpsSourceForm = ({
  config,
  setConfig,
}: {
  config: HttpsConfig
  setConfig: React.Dispatch<React.SetStateAction<HttpsConfig>>
}) => (
  <div style={{display: 'flex', flexDirection: 'column', gap: '1rem'}}>
    <div>
      <label className='form-label fw-semibold'>Download URLs</label>
      <textarea
        autoFocus
        className='form-control'
        rows={5}
        placeholder={
          'https://ftp.ensembl.org/pub/release-112/fasta/homo_sapiens/dna/README\nhttps://example.com/data/sample.fastq.gz'
        }
        value={config.urls}
        onChange={(e) => setConfig((prev) => ({...prev, urls: e.target.value}))}
      />
      <div className='form-text'>
        Enter one URL per line. Each file will be downloaded and saved to your storage with the
        filename from the URL.
      </div>
    </div>
    <div>
      <label className='form-label fw-semibold'>
        Authorization Token <span className='text-muted'>(optional)</span>
      </label>
      <input
        className='form-control form-control-sm'
        type='password'
        placeholder='Bearer token or API key'
        value={config.authToken}
        onChange={(e) => setConfig((prev) => ({...prev, authToken: e.target.value}))}
        autoComplete='off'
      />
      <div className='form-text'>
        Sent as <code>Authorization: Bearer &lt;token&gt;</code> header. Leave empty for public
        URLs.
      </div>
    </div>
  </div>
)

/* ---------- FTP source sub-form ---------- */

const FtpSourceForm = ({
  config,
  setConfig,
}: {
  config: FtpConfig
  setConfig: React.Dispatch<React.SetStateAction<FtpConfig>>
}) => {
  const handleHostChange = (value: string) => {
    const parsed = parseFtpUrl(value)
    if (parsed) {
      setConfig((prev) => ({...prev, host: parsed.host, port: parsed.port, path: parsed.path}))
    } else {
      setConfig((prev) => ({...prev, host: value}))
    }
  }

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: '1rem'}}>
      <div style={{display: 'grid', gridTemplateColumns: '1fr 96px', gap: '0.75rem'}}>
        <div>
          <label className='form-label fw-semibold'>FTP Host or URL</label>
          <input
            autoFocus
            className='form-control'
            type='text'
            placeholder='ftp.ncbi.nlm.nih.gov or ftp://ftp.ncbi.nlm.nih.gov/genomes/'
            value={config.host}
            onChange={(e) => handleHostChange(e.target.value)}
          />
          <div className='form-text'>
            Paste a full FTP/HTTPS URL (e.g.{' '}
            <code>ftp://ftp.ncbi.nlm.nih.gov/genomes/</code>) and host, port, and path will be
            filled automatically.
          </div>
        </div>
      <div>
        <label className='form-label fw-semibold'>Port</label>
        <input
          className='form-control'
          type='number'
          value={config.port}
          onChange={(e) => setConfig((prev) => ({...prev, port: parseInt(e.target.value) || 21}))}
        />
      </div>
    </div>
    <div>
      <label className='form-label fw-semibold'>Starting Path</label>
      <input
        className='form-control'
        type='text'
        placeholder='/pub/datasets/'
        value={config.path}
        onChange={(e) => setConfig((prev) => ({...prev, path: e.target.value}))}
      />
    </div>
    <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem'}}>
      <div>
        <label className='form-label fw-semibold'>Username</label>
        <input
          className='form-control form-control-sm'
          type='text'
          value={config.user}
          onChange={(e) => setConfig((prev) => ({...prev, user: e.target.value}))}
        />
      </div>
      <div>
        <label className='form-label fw-semibold'>Password</label>
        <input
          className='form-control form-control-sm'
          type='password'
          value={config.password}
          onChange={(e) => setConfig((prev) => ({...prev, password: e.target.value}))}
          autoComplete='off'
        />
      </div>
    </div>
    <div className='form-check'>
      <input
        className='form-check-input'
        type='checkbox'
        id='ftp-secure'
        checked={config.secure}
        onChange={(e) => setConfig((prev) => ({...prev, secure: e.target.checked}))}
      />
      <label className='form-check-label' htmlFor='ftp-secure'>
        Use FTPS (TLS)
      </label>
    </div>
    <div className='form-text'>
      You will browse the FTP server in the next step to select files.
    </div>
  </div>
  )
}

/* ---------- Step 2: File Browser ---------- */

interface FileBrowserProps {
  items: BrowseItem[]
  selectedKeys: Set<string>
  browsePath: string[]
  isLoading: boolean
  error: string | null
  onNavigate: (path: string) => void
  onNavigateUp: () => void
  onToggle: (key: string) => void
  onSelectAll: () => void
}

const FileBrowser = ({
  items,
  selectedKeys,
  browsePath,
  isLoading,
  error,
  onNavigate,
  onNavigateUp,
  onToggle,
  onSelectAll,
}: FileBrowserProps) => {
  if (error) {
    return (
      <div className='alert alert-danger d-flex align-items-center' role='alert'>
        <i className='fas fa-exclamation-triangle me-2'></i>
        {error}
      </div>
    )
  }

  const currentDisplay =
    browsePath.length > 0
      ? browsePath[browsePath.length - 1].replace(/\/$/, '').split('/').filter(Boolean).join(' / ')
      : '/'

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: '0.5rem'}}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.25rem 0',
          fontSize: 'var(--ob-font-size-sm)',
          color: 'var(--kt-text-muted)',
        }}
      >
        {browsePath.length > 0 && (
          <button
            className='btn btn-sm btn-light-primary'
            onClick={onNavigateUp}
            style={{padding: '2px 8px', fontSize: 'var(--ob-font-size-xs)'}}
          >
            <i className='fas fa-arrow-up me-1'></i> Up
          </button>
        )}
        <i className='fas fa-folder-open'></i>
        <span>{currentDisplay}</span>
        <span style={{marginLeft: 'auto'}}>
          {items.length} item(s) | {selectedKeys.size} selected
        </span>
      </div>

      <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: 'var(--ob-font-size-sm)'}}>
        <input
          type='checkbox'
          className='form-check-input'
          checked={items.length > 0 && selectedKeys.size === items.length}
          onChange={onSelectAll}
          style={{cursor: 'pointer'}}
        />
        <span style={{fontWeight: 500}}>Select all</span>
      </div>

      {isLoading ? (
        <div style={{display: 'flex', justifyContent: 'center', padding: '2rem'}}>
          <span className='spinner-border spinner-border-sm me-2'></span>
          Loading...
        </div>
      ) : items.length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            padding: '2rem',
            color: 'var(--kt-text-muted)',
          }}
        >
          <i className='fas fa-folder-open' style={{fontSize: '2rem', opacity: 0.3}}></i>
          <p style={{marginTop: '0.5rem'}}>No files or folders found at this location.</p>
        </div>
      ) : (
        <div
          style={{
            border: '1px solid var(--kt-border-color)',
            borderRadius: '0.375rem',
            overflow: 'hidden',
          }}
        >
          <table className='table table-hover table-sm mb-0'>
            <thead>
              <tr style={{fontSize: 'var(--ob-font-size-xs)', fontWeight: 600, textTransform: 'uppercase'}}>
                <th style={{width: '40px'}}></th>
                <th>Name</th>
                <th style={{width: '100px', textAlign: 'right'}}>Size</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr
                  key={item.path}
                  style={{cursor: 'pointer', fontSize: 'var(--ob-font-size-sm)'}}
                  onClick={() => (item.isDir ? onNavigate(item.path) : onToggle(item.path))}
                >
                  <td style={{textAlign: 'center', verticalAlign: 'middle'}}>
                    <input
                      type='checkbox'
                      className='form-check-input'
                      checked={selectedKeys.has(item.path)}
                      onChange={(e) => {
                        e.stopPropagation()
                        onToggle(item.path)
                      }}
                      onClick={(e) => e.stopPropagation()}
                      style={{cursor: 'pointer'}}
                    />
                  </td>
                  <td style={{verticalAlign: 'middle'}}>
                    <i
                      className={
                        item.isDir ? 'fas fa-folder text-warning me-2' : 'fas fa-file me-2'
                      }
                      style={{fontSize: 'var(--ob-font-size-base)'}}
                    ></i>
                    {item.name}
                    {item.isDir && (
                      <i
                        className='fas fa-chevron-right ms-2'
                        style={{fontSize: 'var(--ob-font-size-xs)', opacity: 0.4}}
                      ></i>
                    )}
                  </td>
                  <td
                    style={{
                      textAlign: 'right',
                      verticalAlign: 'middle',
                      color: 'var(--kt-text-muted)',
                    }}
                  >
                    {item.isDir ? '' : formatBytes(item.size || 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

/* ---------- Step 3 (or 2 for HTTPS): Import Summary ---------- */

interface ImportSummaryProps {
  sourceType: ImportSourceType
  s3Config: S3Config
  httpsConfig: HttpsConfig
  ftpConfig: FtpConfig
  selectedItems: BrowseItem[]
  selectedKeys: Set<string>
  destination: string
  isImporting: boolean
}

const ImportSummary = ({
  sourceType,
  s3Config,
  httpsConfig,
  ftpConfig,
  selectedItems,
  selectedKeys,
  destination,
  isImporting,
}: ImportSummaryProps) => {
  const httpsUrls =
    sourceType === 'https'
      ? httpsConfig.urls
          .split('\n')
          .map((u) => u.trim())
          .filter(Boolean)
      : []

  const totalSize =
    sourceType === 'https' ? 0 : selectedItems.reduce((sum, item) => sum + (item.size || 0), 0)
  const fileCount =
    sourceType === 'https' ? httpsUrls.length : selectedItems.filter((i) => !i.isDir).length
  const folderCount =
    sourceType === 'https' ? 0 : selectedItems.filter((i) => i.isDir).length

  const sourceDisplay =
    sourceType === 's3'
      ? s3Config.s3Uri
      : sourceType === 'https'
        ? `${httpsUrls.length} URL(s)`
        : `ftp://${ftpConfig.host}${ftpConfig.path}`

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: '1rem'}}>
      {isImporting && (
        <div className='alert alert-light-primary d-flex align-items-center p-3'>
          <span className='spinner-border spinner-border-sm me-2'></span>
          Downloading and copying files to your storage. This may take a few minutes for large
          files.
        </div>
      )}

      <div
        style={{display: 'grid', gridTemplateColumns: '140px 1fr', gap: '0.5rem', fontSize: 'var(--ob-font-size-base)'}}
      >
        <span style={{fontWeight: 600, color: 'var(--kt-text-muted)'}}>Source:</span>
        <span>
          <code>{sourceDisplay}</code>
        </span>
        <span style={{fontWeight: 600, color: 'var(--kt-text-muted)'}}>Protocol:</span>
        <span>{sourceType.toUpperCase()}</span>
        <span style={{fontWeight: 600, color: 'var(--kt-text-muted)'}}>Destination:</span>
        <span>
          <code>{destination.split('/').filter(Boolean).slice(1).join('/') || '/'}</code>
        </span>
      </div>

      <hr style={{margin: '0.25rem 0'}} />

      <div style={{fontSize: 'var(--ob-font-size-base)', fontWeight: 600}}>
        {fileCount > 0 && `${fileCount} file(s)`}
        {fileCount > 0 && folderCount > 0 && ', '}
        {folderCount > 0 && `${folderCount} folder(s)`}
        {totalSize > 0 && ` (${formatBytes(totalSize)} total)`}
      </div>

      <div
        style={{
          maxHeight: '200px',
          overflowY: 'auto',
          border: '1px solid var(--kt-border-color)',
          borderRadius: '0.375rem',
          padding: '0.5rem',
        }}
      >
        {sourceType === 'https'
          ? httpsUrls.map((url) => (
              <div
                key={url}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.25rem 0',
                  fontSize: 'var(--ob-font-size-sm)',
                }}
              >
                <i className='fas fa-link' style={{fontSize: 'var(--ob-font-size-sm)', width: '16px', textAlign: 'center'}}></i>
                <span
                  style={{
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {url}
                </span>
              </div>
            ))
          : Array.from(selectedKeys).map((key) => {
              const item = selectedItems.find((i) => i.path === key)
              return (
                <div
                  key={key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.25rem 0',
                    fontSize: 'var(--ob-font-size-sm)',
                  }}
                >
                  <i
                    className={item?.isDir ? 'fas fa-folder text-warning' : 'fas fa-file'}
                    style={{fontSize: 'var(--ob-font-size-sm)', width: '16px', textAlign: 'center'}}
                  ></i>
                  <span
                    style={{
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item?.name || key}
                  </span>
                  {!item?.isDir && (
                    <span style={{color: 'var(--kt-text-muted)', fontSize: 'var(--ob-font-size-xs)'}}>
                      {formatBytes(item?.size || 0)}
                    </span>
                  )}
                </div>
              )
            })}
      </div>
    </div>
  )
}
